/**
 * @file backup-install.ts
 * @description Правила блока «Установка на сервере» (phase19.26 шаг 7.1, Р1):
 * проверка папки локальных копий и обратный отсчёт команды установки.
 *
 * Папка проверяется по тем же правилам, что и на сервере: абсолютный путь
 * внутри `/srv/`, `/mnt/` или `/backup/`, минимум один каталог после префикса,
 * сегменты из латиницы, цифр, `.`, `_`, `-`, без `.`/`..`, без `/` в конце,
 * не длиннее 200 символов. Сервер проверяет ещё раз (400).
 */

/** Папка копий по умолчанию — совпадает с дефолтом API и скрипта установки. */
export const DEFAULT_BACKUP_ROOT = "/srv/chaiberi/backups";

/** Корни, внутри которых разрешена папка копий. */
const ALLOWED_ROOTS = ["srv", "mnt", "backup"];
const SEGMENT_RE = /^[A-Za-z0-9._-]+$/;
const MAX_LENGTH = 200;

/** Текст ошибки папки копий или null — папка подходит. */
export function backupRootError(raw: string): string | null {
  const value = raw.trim();
  if (!value) return "Укажите папку для копий.";
  if (value.length > MAX_LENGTH) return `Путь длиннее ${MAX_LENGTH} символов.`;
  if (!value.startsWith("/")) return "Путь должен начинаться с «/», например /srv/chaiberi/backups.";
  if (value.endsWith("/")) return "Уберите «/» в конце пути.";
  const segments = value.slice(1).split("/");
  if (!ALLOWED_ROOTS.includes(segments[0] ?? "")) return "Папка должна быть внутри /srv, /mnt или /backup.";
  if (segments.length < 2) return `Укажите папку внутри /${segments[0]}, например /${segments[0]}/chaiberi/backups.`;
  for (const segment of segments) {
    if (!segment) return "В пути не должно быть «//».";
    if (segment === "." || segment === "..") return "В пути не должно быть «.» и «..».";
    if (!SEGMENT_RE.test(segment)) {
      return "Только латинские буквы, цифры, точка, дефис и подчёркивание.";
    }
  }
  return null;
}

/**
 * Начальное значение поля «Папка для копий»: папка из отчёта агента (чтобы
 * переустановка не переносила копии), если она подходит; иначе по умолчанию.
 */
export function installFolderFrom(localRoot: string | null): string {
  return localRoot && !backupRootError(localRoot) ? localRoot : DEFAULT_BACKUP_ROOT;
}

/** Сколько осталось действовать команде: мс (≤ 0 — истекла). */
export function installMsLeft(expiresAt: string, now: number): number {
  const end = new Date(expiresAt).getTime();
  return Number.isFinite(end) ? end - now : 0;
}

/** Остаток времени «59 мин 05 с» / «45 с». */
export function formatTimeLeft(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return min > 0 ? `${min} мин ${String(sec).padStart(2, "0")} с` : `${sec} с`;
}
